import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import {
  CameraAlt as CameraIcon,
  Close as CloseIcon,
} from '@mui/icons-material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import { typeScale } from '@/shared/styles/typography'

import { toResizedJpegBlob } from './toResizedJpeg'

interface Props {
  open: boolean
  onClose: () => void
  onCapture: (image: Blob) => void
}

// 画面内カメラで資料 (カルテ・お薬手帳・残薬・問診票) を撮る modal。撮るだけで、ファイルを選ぶ口は持たない
// (選ぶのは modal の外の [⬆]。hub log/108 決定 36)。
// (nasu-patient-app-front CameraCaptureModal を yakureki-front latest の token に合わせて移植)
// 撮った後も閉じず、続けて撮れる (3 枚の問診票は shutter × 3 → ✕)。枚数を shutter の下に出す。
// 撮った物は撮った時点で付いているので「完了」の釦は無く、閉じるのは ✕ (Escape・backdrop も同じ)。
// カメラが起動できない (権限なし・AnyCOMPASS の iframe) ときは 1 行で言い、shutter は押せない。
// 1 枚で済む呼び出し側 (栄養の問診票の読み取り) は onCapture で自分で閉じる。
export const CameraCaptureModal = ({ open, onClose, onCapture }: Props) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  // この modal を開いてから撮った枚数
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!open) return

    let stopped = false

    // 閉じるときに表示を初めに戻す (次に開いたときに前回の撮影可・エラー・枚数を持ち越さない)
    const stop = () => {
      stopped = true
      setReady(false)
      setError(null)
      setCount(0)
      const stream = streamRef.current
      if (stream) {
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
      const video = videoRef.current
      if (video) video.srcObject = null
    }

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        })
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (!video) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        video.srcObject = stream
        await video.play()
        setReady(true)
      } catch {
        if (!stopped) setError('カメラを起動できませんでした。')
      }
    }

    start()
    return () => stop()
  }, [open])

  const handleShutter = async () => {
    const video = videoRef.current
    if (!video || !ready) return
    try {
      const blob = await toResizedJpegBlob(video)
      setError(null)
      setCount((c) => c + 1)
      onCapture(blob)
    } catch (e) {
      setError(e instanceof Error ? e.message : '撮影に失敗しました')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: NOTION_DIALOG_PAPER_SX } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        撮る
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="閉じる"
          sx={{
            color: colorTokens.fg.muted,
            '&:hover': { backgroundColor: colorTokens.bg.muted },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: '100%',
            minHeight: 300,
            position: 'relative',
            bgcolor: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            component="video"
            ref={videoRef}
            playsInline
            muted
            autoPlay
            sx={{ width: '100%', maxHeight: 480, objectFit: 'contain' }}
          />
        </Box>
        {error && (
          <Typography
            align="center"
            sx={{ mt: 2, color: colorTokens.state.dangerFg }}
          >
            {error}
          </Typography>
        )}
        {/* shutter は丸い icon 1 つ。塗り (primary) はこの画面の主 CTA なのでここにだけ */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <IconButton
            aria-label="撮影"
            onClick={() => void handleShutter()}
            disabled={!ready}
            sx={{
              width: 56,
              height: 56,
              bgcolor: colorTokens.accent.primary,
              color: colorTokens.accent.primaryFg,
              '&:hover': { bgcolor: colorTokens.accent.primaryHover },
              '&:focus-visible': { boxShadow: colorTokens.focus.ringShadow },
              '&.Mui-disabled': {
                bgcolor: colorTokens.bg.muted,
                color: colorTokens.fg.faint,
              },
            }}
          >
            <CameraIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>
        {count > 0 && (
          <Typography
            align="center"
            sx={{ ...typeScale.label, color: colorTokens.fg.muted, mt: 1.5 }}
          >
            {count} 枚を付けました
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  )
}
